import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import Slider from "react-slick";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import SliderArrow from "../../components/common/SliderArrows";
import { useSelector } from "react-redux";
import { InlineIcon } from "@iconify/react";
import { Tooltip, Whisper } from "rsuite";
import { DateTime } from "luxon";
import { useHotelsListLimit } from "../../hooks";
import jssVariables, { buttonStylesType1 } from "../../jss_setup";
import RatingPanel from "../../components/common/RatingPanel";
import { Button } from "../../components/common/Button";

const imgCarouselStyles = createUseStyles({
  imgs: (props) => ({
    position: "relative",
    display: "grid",
    width: "100%",
    height: "35vh",
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.imgs", {}),
  }),
  slider: (props) => ({
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    "& .slick-prev": {
      position: "absolute",
      left: jssVariables.normal_gap,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 1,
    },
    "& .slick-next": {
      position: "absolute",
      right: jssVariables.normal_gap,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 1,
    },
    ..._.get(props, "customStyles.slider", {}),
  }),
  img: (props) => ({
    width: "100%",
    height: "35vh",
    objectFit: "cover",
    zIndex: -1,
    borderRadius: 0,
    ..._.get(props, "customStyles.img", {}),
  }),
  tags: (props) => ({
    alignSelf: "end",
    justifySelf: "start",
    display: "grid",
    gridTemplateColumns: "repeat(10, max-content)",
    overflow: "auto",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    zIndex: 0,
    transform: "translateY(-50%)",
    position: props.isMarketplace ? "absolute" : "relative",
    top: props.isMarketplace ? `calc(${jssVariables.normal_gap} * 2)` : "unset",
    left: props.isMarketplace ? jssVariables.normal_gap : "unset",
    width: "100%",
    ..._.get(props, "customStyles.tags", {}),
  }),
  tag: (props) => ({
    color: jssVariables.colors.text.light,
    background: props.tagColor,
    padding: `calc(${jssVariables.normal_gap} / 4) calc(${jssVariables.normal_gap} / 2) calc(${jssVariables.normal_gap} / 4) calc(${jssVariables.normal_gap} / 2)`,
    borderRadius: `calc(${jssVariables.normal_gap} / 2)`,
    fontSize: "medium",
    ..._.get(props, "customStyles.tag", {}),
  }),
  rating: (props) => ({
    position: "absolute",
    transform: "translateY(50%)",
    right: jssVariables.normal_gap,
    bottom: props.isMarketplace ? "-3rem" : "0rem",
    ..._.get(props, "customStyles.rating", {}),
  }),
  provider: (props) => ({
    position: "absolute",
    left: jssVariables.normal_gap,
    bottom: "-2rem",
    filter: "drop-shadow(2px 2px 20px rgba(0, 0, 0, 0.2))",
    ..._.get(props, "customStyles.provider", {}),
  }),
  provider_logo: (props) => ({
    border: `2px solid ${props.baseColor}`,
    borderRadius: "50%",
    width: "4rem",
    height: "4rem",
    objectFit: "scale-down",
    zIndex: 1,
    background: "white",
    padding: ".1rem",
    ..._.get(props, "customStyles.provider_logo", {}),
  }),
  provider_name: (props) => ({
    position: "absolute",
    color: props.baseColor,
    padding: `calc(${jssVariables.normal_gap} / 4) ${jssVariables.normal_gap}`,
    background: "white",
    borderRadius: "0 5px 5px 0",
    marginLeft: `calc(-0.5 * ${jssVariables.normal_gap})`,
    zIndex: -1,
    top: "50%",
    transform: "translateY(-50%)",
    width: "max-content",
    ..._.get(props, "customStyles.provider_name", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    imgs: (props) => ({
      height: "40vh",
      ..._.get(props, "customStyles.imgs.smallscreen", {}),
    }),
    img: (props) => ({
      height: "40vh",
      objectPosition: "center",
      ..._.get(props, "customStyles.img.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    rating: (props) => ({
      bottom: 0,
      ..._.get(props, "customStyles.rating.bigscreen", {}),
    }),
  },
});
export const ImageCarousel = (props) => {
  const {
    activity,
    baseColor,
    tagColor,
    isMarketplace,
    showRating,
    customStyles,
    excludeTag,
  } = props;

  const { type } = useContext(StyleTypeContext);
  const classes = imgCarouselStyles({
    type,
    baseColor,
    tagColor,
    isMarketplace,
    customStyles,
  });

  const imgs = activity.images.map((img) => _.get(img, "hd"));

  const carouselSize = 4;

  const settings = {
    arrows: true,
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: imgs.length >= carouselSize ? carouselSize : imgs.length,
    slidesToScroll: imgs.length >= carouselSize ? carouselSize : imgs.length,
    nextArrow: <SliderArrow icon="fa:angle-right" />,
    prevArrow: <SliderArrow icon="fa:angle-left" />,
    responsive: [
      {
        breakpoint: 1023,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
        },
      },
    ],
  };

  return (
    <div className={classes.imgs}>
      <Slider className={classes.slider} {...settings}>
        {imgs
          .filter((img, idx) => idx < 6)
          .map((img, idx) => (
            <img
              alt={img.title}
              src={img.url}
              key={idx}
              className={classes.img}
            />
          ))}
      </Slider>
      {/* <ShareBtn
        path={`/activity/${activity.product_type}_${activity.reference}`}
      /> */}
      <div className={classes.tags}>
        {[
          ..._.get(activity, "categories", []).map((obj) => obj.label),
          ..._.get(activity, "subcategories", []).map((obj) => obj.label),
          ..._.get(activity, "tags", []).map((obj) => obj.label),
        ]
          .filter((tag) =>
            excludeTag
              ? _.trim(tag.toLowerCase()) !== _.trim(excludeTag.toLowerCase())
              : true
          )
          .map((cat, idx) => (
            <small key={idx} className={classes.tag}>
              <strong>{_.startCase(cat)}</strong>
            </small>
          ))}
      </div>
      {showRating && type !== "type2" ? (
        <div className={classes.rating}>
          <RatingPanel
            rating={Number.parseInt(activity.internal_rating)}
            fontSize="large"
          />
        </div>
      ) : null}
      {isMarketplace ? (
        <div className={classes.provider}>
          <img
            className={classes.provider_logo}
            src={_.get(
              activity,
              "provider.logo.thumbnail_url",
              "https://apollo-statics.s3.eu-central-1.amazonaws.com/images/image_placeholder.jpg"
            )}
            alt=""
          />
          <span className={classes.provider_name}>
            <strong>{_.get(activity, "provider.name")}</strong>
          </span>
        </div>
      ) : null}
    </div>
  );
};
ImageCarousel.defaultProps = {
  customStyles: {},
  showRating: true,
  baseColor: jssVariables.colors.base_color,
  isMarketplace: false,
};
ImageCarousel.propTypes = {
  customStyles: PropTypes.object,
  showRating: PropTypes.bool,
  baseColor: PropTypes.string,
  activity: PropTypes.object.isRequired,
  isMarketplace: PropTypes.bool.isRequired,
  excludeTag: PropTypes.string,
  tagColor: PropTypes.string,
};

export function getAdditionalInfo({ t, activity }) {
  var output = [];
  output.push(["meetingPointInfo", activity?.meeting_point_info ?? ""]);
  output.push(["meetingPoints", activity?.meeting_points ?? []]);
  output.push(["providedBy", activity?.supplier?.supplier_name]);
  output.push([
    "languages",
    (activity?.languages ?? []).map((lng) => _.capitalize(lng.name)).join(", "),
  ]);
  output.push(["expirationDate", activity?.expiration_date ?? ""]);
  output.push(["groupSize", activity?.group_size?.label ?? []]);

  output = output.filter((out) => !_.isEmpty(out[1]));

  return output;
}

const rateDetailsStyles = createUseStyles({
  reviewsDetails: (props) => ({
    margin: jssVariables.spacing.section_margin_mobile,
    ..._.get(props, "customStyles.reviewsDetails", {}),
  }),
  title: (props) => ({
    background: jssVariables.colors.base_color,
    color: "white",
    textTransform: "uppercase",
    padding: `calc(${jssVariables.normal_gap} * 2) 5vw`,
    fontSize: "x-large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.title", {}),
  }),
  content: (props) => ({
    ..._.get(props, "customStyles.content", {}),
  }),
  reviewsContainer: (props) => ({
    display: "grid",
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    padding: `calc(${jssVariables.normal_gap} * 2) 5vw`,
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.content.smallscreen", {}),
  }),
  headerContainer: (props) => ({
    display: "grid",
    padding: `${jssVariables.normal_gap} 5vw`,
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.headerContainer", {}),
  }),
  averageValue: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    gridGap: jssVariables.normal_gap,
    fontSize: "xx-large",
    transform: "translateX(30%)",
    width: "max-content",
    ..._.get(props, "customStyles.title", {}),
  }),
  headerInfo: (props) => ({
    fontSize: "large",
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.title", {}),
  }),
  iconContainer: (props) => ({
    ..._.get(props, "customStyles.iconContainer", {}),
  }),
  icon: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "xx-large",
    ..._.get(props, "customStyles.icon", {}),
  }),
  infoIcon: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "x-large",
    ..._.get(props, "customStyles.infoIcon", {}),
  }),
  iconSmall: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "large",
    ..._.get(props, "customStyles.iconSmall", {}),
  }),
  details: (props) => ({
    display: "grid",
    padding: `${jssVariables.normal_gap} 5vw`,
    gridTemplateColumns: "repeat(2, max-content)",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.details", {}),
  }),
  rating: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    gridGap: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.details", {}),
  }),
  starsContainer: (props) => ({
    display: "grid",
    alignContent: "space-between",
    ..._.get(props, "customStyles.starsContainer", {}),
  }),
  stars: (props) => ({
    justifySelf: "end",
    ..._.get(props, "customStyles.stars", {}),
  }),
  textContainer: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.categoryText", {}),
  }),
  categoryText: (props) => ({
    display: "grid",
    fontSize: "large",
    textTransform: "capitalize",
    ..._.get(props, "customStyles.categoryText", {}),
  }),
  noReviewsContainer: (props) => ({
    fontSize: "large",
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    padding: `calc(${jssVariables.normal_gap} * 2) 5vw`,
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.noReviewsContainer", {}),
  }),
  noReviewsText: (props) => ({
    ..._.get(props, "customStyles.noReviewsText", {}),
  }),
  iconSmallNoReviews: (props) => ({
    display: "grid",
    fontSize: "x-large",
    textTransform: "capitalize",
    color: "lightgray",
    ..._.get(props, "customStyles.iconSmallNoReviews", {}),
  }),
  expandReviewsBtnContainer: (props) => ({
    display: "grid",
    justifyContent: "center",
    textAlign: "center",
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.expandReviewsBtnContainer", {}),
  }),
  expandReviewsBtn: (props) => ({
    ...buttonStylesType1,
    fontSize: "large",
    textTransform: "capitalize",
    ..._.get(props, "customStyles.expandReviewsBtn", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    reviewsDetails: (props) => ({
      padding: "0 25vw 0 15vw",
      ..._.get(props, "customStyles.reviewsDetails.smallscreen", {}),
    }),
    title: (props) => ({
      padding: `calc(${jssVariables.normal_gap} * 2) `,
      ..._.get(props, "customStyles.title", {}),
    }),
    content: (props) => ({
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    reviewsContainer: (props) => ({
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      padding: `calc(${jssVariables.normal_gap} * 2) 1.5vw`,
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    headerContainer: (props) => ({
      ..._.get(props, "customStyles.headerContainer.smallscreen", {}),
    }),
    details: (props) => ({
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      paddingLeft: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.details.smallscreen", {}),
    }),
    averageValue: (props) => ({
      fontSize: "xxx-large",
      paddingLeft: `calc(${jssVariables.normal_gap} * 5)`,
      transform: "translateX(0%)",
      ..._.get(props, "customStyles.title.smallscreen", {}),
    }),
    noReviewsContainer: (props) => ({
      fontSize: "large",
      display: "grid",
      gridTemplateColumns: "max-content 1fr",
      paddingLeft: `calc(${jssVariables.normal_gap} * 1.5)`,
      gridGap: jssVariables.normal_gap,
      ..._.get(props, "customStyles.noReviewsContainer.smallscreen", {}),
    }),
    noReviewsText: (props) => ({
      paddingTop: `calc(${jssVariables.normal_gap} / 5)`,
      ..._.get(props, "customStyles.noReviewsText.smallscreen", {}),
    }),
    icon: (props) => ({
      fontSize: "xxx-large",
      ..._.get(props, "customStyles.icon.smallscreen", {}),
    }),
    textContainer: (props) => ({
      gridGap: `calc(${jssVariables.normal_gap} *1)`,
      ..._.get(props, "customStyles.categoryText", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    reviewsDetails: (props) => ({
      padding: jssVariables.spacing.center_padding_large,
      ..._.get(props, "customStyles.reviewsDetails.smallscreen", {}),
    }),
  },
});
export const ReviewsDetails = () => {
  const pageSize = 2;

  const { listLimit, increaseListSize } = useHotelsListLimit({
    defaultPageSize: pageSize,
  });

  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const classes = rateDetailsStyles({
    type,
    customStyles: cStyler({
      type,
      brand,
      objName: "rateDetails",
    }),
  });
  const { loading, ratings, reviews } = useSelector((state) => {
    return {
      loading: _.get(state.ActivitySelected, "loading", true),
      ratings: _.get(state.ActivitySelected, "activity.ratings", {}),
      reviews: _.get(state.ActivitySelected, "activity.reviews", []),
    };
  });

  const visibleReviews = reviews.filter((r, idx) => idx < listLimit);

  return (
    !loading && (
      <div className={classes.reviewsDetails}>
        <div className={classes.title}>{t("reviews")}</div>
        {_.get(ratings, "total_ratings", 0) ? (
          <React.Fragment>
            <div className={classes.content}>
              <div className={classes.headerContainer}>
                <span className={classes.averageValue}>
                  <span className={classes.iconContainer}>
                    <InlineIcon
                      className={classes.icon}
                      icon="carbon:star-filled"
                    />
                  </span>
                  {ratings.average_rating}/5
                </span>
                <span className={classes.headerInfo}>
                  {`${t("basedOn")} ${ratings.total_ratings} ${t(
                    "reviewsFromVerCustomers"
                  )}`}
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={
                      <Tooltip>{t("onlyBookedCustomersTooltip")}</Tooltip>
                    }>
                    <InlineIcon
                      className={classes.infoIcon}
                      icon="dashicons:info"
                    />
                  </Whisper>{" "}
                </span>
              </div>
              <div className={classes.details}>
                <span className={classes.starsContainer}>
                  <span className={classes.stars}>
                    <RatingStars numOfStars={5} />
                  </span>
                  <span className={classes.stars}>
                    <RatingStars numOfStars={4} />
                  </span>
                  <span className={classes.stars}>
                    <RatingStars numOfStars={3} />
                  </span>
                  <span className={classes.stars}>
                    <RatingStars numOfStars={2} />
                  </span>
                  <span className={classes.stars}>
                    <RatingStars numOfStars={1} />
                  </span>
                </span>
                <span className={classes.textContainer}>
                  <span className={classes.categoryText}>
                    {t("excellent")} ({ratings.rating_5})
                  </span>
                  <span className={classes.categoryText}>
                    {t("great")} ({ratings.rating_4})
                  </span>
                  <span className={classes.categoryText}>
                    {t("average")} ({ratings.rating_3})
                  </span>
                  <span className={classes.categoryText}>
                    {t("poor")} ({ratings.rating_2})
                  </span>
                  <span className={classes.categoryText}>
                    {t("bad")} ({ratings.rating_1})
                  </span>
                </span>
              </div>
            </div>
            <div className={classes.reviewsContainer}>
              {visibleReviews.map((rev, idx) => (
                <Review review={rev} key={idx} />
              ))}
            </div>
            {!loading && reviews.length > listLimit && (
              <div className={classes.expandReviewsBtnContainer}>
                <Button
                  text={t("showMore")}
                  onClick={() => increaseListSize()}
                />
              </div>
            )}
          </React.Fragment>
        ) : (
          <div className={classes.noReviewsContainer}>
            <InlineIcon
              className={classes.iconSmallNoReviews}
              icon="carbon:star-filled"
            />
            <span className={classes.noReviewsText}> {t("noReviewsYet")}</span>
          </div>
        )}
      </div>
    )
  );
};
ReviewsDetails.defaultProps = {};
ReviewsDetails.propTypes = {};

const ratingStarsStyles = createUseStyles({
  ratingStars: (props) => ({
    ..._.get(props, "customStyles.ratingStars", {}),
  }),
  iconSmall: (props) => ({
    color: jssVariables.colors.base_color,
    fontSize: "large",
    ..._.get(props, "customStyles.iconSmall", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
});
export const RatingStars = ({ numOfStars }) => {
  const { type } = useContext(StyleTypeContext);
  const classes = ratingStarsStyles({
    type,
  });
  const arr = new Array(numOfStars).fill(0);

  return (
    <div className={classes.ratingStars}>
      {arr.map((a, idx) => (
        <InlineIcon
          className={classes.iconSmall}
          icon="carbon:star-filled"
          key={idx}
        />
      ))}
    </div>
  );
};
RatingStars.defaultProps = {
  numOfStars: 0,
};
RatingStars.propTypes = {
  numOfStars: PropTypes.number.isRequired,
};

const reviewStyles = createUseStyles({
  review: (props) => ({
    background: jssVariables.colors.background.darker,
    borderRadius: "10px",
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    fontSize: "medium",
    ..._.get(props, "customStyles.review", {}),
  }),
  header: (props) => ({
    textTransform: "capitalize",
    ..._.get(props, "customStyles.header", {}),
  }),
  customerName: (props) => ({
    fontSize: "x-large",
    ..._.get(props, "customStyles.customerName", {}),
  }),
  rate: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    gridGap: jssVariables.half_gap,
    alignItems: "baseline",
    ..._.get(props, "customStyles.rate", {}),
  }),

  [`@media ${jssVariables.media.smallscreen}`]: {},
});
export const Review = ({ review }) => {
  const { t } = useContext(TranslationContext);

  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = {
    ...cStyler({
      type,
      brand,
      objName: "activityReview",
    }),
  };
  const classes = reviewStyles({
    type,
    customStyles,
  });

  return (
    <div className={classes.review}>
      <div className={classes.header}>
        <span className={classes.customerName}> {t("anonymous")} </span>
        {`${t("from")} ${review.customer_country} - ${DateTime.fromISO(
          review.date
        ).toLocaleString(DateTime.DATE_MED)}`}
      </div>
      <div className={classes.rate}>
        <RatingPanel
          rating={Number.parseInt(Math.floor(review.rating / 2))}
          customStyles={customStyles}
          fontSize="large"
        />
        {Number.parseInt(Math.floor(review.rating / 2))}/5
      </div>
      <div className={classes.comment}>{review.comment}</div>
    </div>
  );
};
RatingStars.defaultProps = {
  review: {},
};
RatingStars.propTypes = {
  review: PropTypes.object.isRequired,
};
